/**
 * Shared helpers for the draft-vs-cloud conflict resolution UX.
 *
 * When resolveDraftRecovery() returns 'conflict', the editor blocks behind
 * DraftConflictModal and the user picks which version to keep. The "view cloud
 * version in a new tab" action reopens the same editor URL with a marker that
 * tells the provider to skip local recovery and show the server copy as-is.
 */

/** Captured data needed to render the conflict modal and apply "keep local". */
export interface DraftConflictData<TDoc> {
  localDocument: TDoc;
  localUndo: TDoc[];
  localRedo: TDoc[];
  /** Epoch ms of the local draft's last save. */
  localTimestamp: number;
  /** Epoch ms of the server document's last_modified (0 if unknown). */
  serverTimestamp: number;
  /** Item count of the local draft (cues/segments), for display. */
  localCount?: number;
  /** Item count of the server document, for display. */
  serverCount?: number;
}

const CLOUD_SOURCE_PARAM = 'source';
const CLOUD_SOURCE_VALUE = 'cloud';

/**
 * True when the current URL asks to bypass local recovery and load the server
 * copy directly (the "view cloud version" tab). Lets the user inspect the cloud
 * version without their local draft shadowing it.
 */
export function forceCloudSourceRequested(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return (
      new URLSearchParams(window.location.search).get(CLOUD_SOURCE_PARAM) ===
      CLOUD_SOURCE_VALUE
    );
  } catch {
    return false;
  }
}

/** Open the current editor URL in a new tab forced onto the cloud version. */
export function openCloudVersionInNewTab(): void {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(CLOUD_SOURCE_PARAM, CLOUD_SOURCE_VALUE);
    window.open(url.toString(), '_blank', 'noopener');
  } catch {
    /* no-op */
  }
}
